/**
 * Period close.
 *
 * ═══════════════════════════════════════════════════════════════════
 * A PERIOD CLOSES PER MODULE, NOT ALL AT ONCE.
 *
 * The cash book can close while the purchase ledger is still being worked, and
 * purchase ordering stays open regardless — because closing the accounting
 * period must not stop departments raising orders. That is why period status is
 * held per module rather than per period.
 *
 * A close blocked by an outstanding task says which task and why. "You cannot
 * close this period" without a reason is not a control, it is an obstacle. So
 * every line of the checklist carries its own count, its own value and the way
 * through to the records behind it — and a close that goes ahead with items
 * still outstanding is an override that demands a written reason, which is kept
 * in the audit trail with the name of whoever gave it.
 * ═══════════════════════════════════════════════════════════════════
 *
 * TWO ENDPOINTS FEED THIS SCREEN, AND THEY ANSWER DIFFERENT QUESTIONS.
 *
 *   /reference/periods         which ledgers are open, closed or locked, for
 *                              every period, plus the label for each ledger.
 *   /operations/close/:id      the checklist, recomputed from live data on
 *                              every request — nothing here is ticked by hand.
 *
 * Neither one carries the other's answer, so the screen reads both. The close
 * response deliberately does not include per-module status, and the period
 * response deliberately does not include the checklist.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTerms, type Terminology } from '../lib/tenant';
import { date, number, statusLabel, statusTone, todayIso } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  EmptyState,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  Stat,
  TextArea,
} from '../components/ui';

/* ------------------------------------------------------------------ *
 * What the server sends
 * ------------------------------------------------------------------ */

type PeriodStatus = 'future' | 'open' | 'closing' | 'closed' | 'permanently_closed';

interface Period {
  id: string;
  name: string;
  periodNo: number;
  financialYearId: string;
  financialYearCode: string;
  startDate: string;
  endDate: string;
  isAdjustmentPeriod: boolean;
  /** One entry per ledger. A ledger with no explicit row is treated as open. */
  statuses: Record<string, PeriodStatus>;
}

interface PeriodsResponse {
  periods: Period[];
  moduleLabels: Record<string, string>;
}

interface CloseTask {
  code: string;
  name: string;
  description: string;
  category: string;
  severity: 'blocking' | 'advisory';
  /**
   * The server's own description of where the work lives ('/payables/invoices',
   * '/cash/reconciliation'). Those are its vocabulary, not this application's
   * routes, so the screen maps the task code to a real route instead — see
   * taskDestinations below.
   */
  detailLink: string;
  status: string;
  outstandingCount: number;
  outstandingValueMinor: number;
}

interface ControlDifference {
  ledger: string;
  controlBalanceMinor: number;
  subLedgerBalanceMinor: number;
  differenceMinor: number;
}

interface IntegrityDifference {
  accountId: string;
  accountCode: string;
  periodId: string;
  periodName: string;
  storedMovementMinor: number;
  derivedMovementMinor: number;
  differenceMinor: number;
}

interface CloseView {
  period: { id: string; name: string; startDate: string; endDate: string; financialYearCode: string };
  tasks: CloseTask[];
  summary: { total: number; complete: number; blockingOutstanding: number; canClose: boolean };
  blockers: Array<{ code: string; name: string; outstandingCount: number }>;
  integrityCheck: { checked: number; differences: IntegrityDifference[] };
  controlReconciliation: { differences: ControlDifference[]; totalDifferenceMinor: number };
}

/* ------------------------------------------------------------------ *
 * Close vocabulary
 * ------------------------------------------------------------------ */

interface TaskDestination {
  to: string;
  label: string;
  permissions: string[];
}

/**
 * Where the work behind each task actually lives in this application.
 *
 * The server describes each task's detail page in its own vocabulary —
 * '/payables/invoices?status=pending_approval', '/cash/reconciliation',
 * '/procurement/outstanding-receipts' — and none of those are routes this
 * application serves. Following one would land the reader on the dashboard
 * with no explanation, which is worse than no link at all. So the task code is
 * mapped to a real route here, labelled with the same words as the navigation
 * so the reader recognises where they are being sent, and paired with the
 * permission that route requires: somebody without it sees the destination
 * named but not linked, rather than a door that opens onto a refusal.
 *
 * Three tasks are deliberately absent. The control account difference, the
 * integrity check and the period lock itself are all answered on this screen.
 */
function taskDestinations(t: Terminology): Record<string, TaskDestination> {
  return {
    APPROVALS_OUTSTANDING: { to: '/approvals', label: 'My approvals', permissions: ['workflow.view'] },
    GOODS_NOT_RECEIPTED: { to: '/receipts', label: 'Goods receipts', permissions: ['grn.record', 'po.view.all'] },
    INVOICES_UNPOSTED: { to: '/invoices', label: `${t.supplier} invoices`, permissions: ['ap.view'] },
    INVOICES_ON_HOLD: { to: '/invoices', label: `${t.supplier} invoices`, permissions: ['ap.view'] },
    MATCH_DISCREPANCIES: { to: '/invoices', label: `${t.supplier} invoices`, permissions: ['ap.view'] },
    BANK_UNRECONCILED: { to: '/cash', label: 'Cash book', permissions: ['cash.view'] },
    DEPRECIATION: { to: '/assets', label: 'Fixed assets', permissions: ['assets.view'] },
    ACCRUALS: { to: '/ledger', label: 'General ledger', permissions: ['gl.view'] },
    PREPAYMENTS: { to: '/ledger', label: 'General ledger', permissions: ['gl.view'] },
    DRAFT_JOURNALS: { to: '/ledger', label: 'General ledger', permissions: ['gl.view'] },
    INTEGRATION_ERRORS: { to: '/integrations', label: 'Integrations', permissions: ['integration.view'] },
    MANAGEMENT_ACCOUNTS: { to: '/reports/MANAGEMENT_ACCOUNTS', label: 'Management accounts', permissions: ['report.run'] },
  };
}

/**
 * Four tasks answer yes or no rather than counting anything: the server sets
 * their outstanding count to 1 to mean "not done yet". Printing that 1 under a
 * heading of "Outstanding" would read as one stray record somebody has to go
 * and find.
 */
const YES_NO_TASKS = new Set(['DEPRECIATION', 'ACCRUALS', 'PREPAYMENTS', 'MANAGEMENT_ACCOUNTS']);

/**
 * Every task category humanises acceptably on its own except 'lock', which
 * reads as a database flag rather than a stage of the month end.
 */
const CATEGORY_LABELS: Record<string, string> = { lock: 'Period lock' };

function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? statusLabel(category);
}

/**
 * The period the close screen should open on.
 *
 * The period containing today, failing that the earliest period still open in
 * any ledger — which is the one being closed. Opening on the first period the
 * API happens to return would put a demonstration three years into the past.
 */
function currentPeriod(periods: Period[]): Period | undefined {
  const today = todayIso();
  const containing = periods.find((p) => !p.isAdjustmentPeriod && p.startDate <= today && p.endDate >= today);
  if (containing) return containing;
  const stillOpen = periods.filter((p) => Object.values(p.statuses).some((s) => s === 'open' || s === 'closing'));
  return stillOpen[0] ?? periods[periods.length - 1];
}

/**
 * Group the periods by financial year for the selector.
 *
 * Three of them are called "Year-end adjustments" and nothing but the year
 * tells them apart, so the year has to be on screen when the list is open.
 * The API returns periods in year then period order, so the groups fall out in
 * order without sorting anything.
 */
function byFinancialYear(periods: Period[]): Array<[string, Period[]]> {
  const groups: Array<[string, Period[]]> = [];
  for (const period of periods) {
    const last = groups[groups.length - 1];
    if (last && last[0] === period.financialYearCode) last[1].push(period);
    else groups.push([period.financialYearCode, [period]]);
  }
  return groups;
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export function ClosePage() {
  const [chosenId, setChosenId] = useState<string | null>(null);

  const periods = useAsync(() => api.get<PeriodsResponse>('/reference/periods'), []);

  const all = periods.data?.periods ?? [];
  const selected = all.find((p) => p.id === chosenId) ?? currentPeriod(all);

  return (
    <>
      <PageHeader
        title="Period close"
        subtitle="Each ledger closes on its own timetable. Purchase ordering stays open regardless."
        meta={
          selected ? (
            <>
              <span>
                Financial year <strong>{selected.financialYearCode}</strong>
              </span>
              <span>
                {date(selected.startDate)} to {date(selected.endDate)}
              </span>
            </>
          ) : null
        }
        actions={
          /*
           * Rendered from the loaded data rather than through an AsyncPanel: a
           * failure of the period lookup belongs in the body of the page as a
           * proper error with a retry, not squeezed into the header beside the
           * title.
           */
          selected ? (
            <label className="row" style={{ gap: '0.4rem' }}>
              <span className="smaller muted">Period</span>
              <select value={selected.id} onChange={(e) => setChosenId(e.currentTarget.value)}>
                {byFinancialYear(all).map(([yearCode, group]) => (
                  <optgroup key={yearCode} label={`Financial year ${yearCode}`}>
                    {group.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
          ) : null
        }
      />

      <AsyncPanel state={periods}>
        {(data) =>
          !selected ? (
            <EmptyState title="No accounting periods have been set up yet">
              <p>
                Periods are created with the financial year. A finance administrator opens one before anything can be
                posted or closed.
              </p>
            </EmptyState>
          ) : (
            /*
             * Keyed on the period so that changing period starts cleanly: any
             * open dialog and any message about the last ledger closed belong
             * to the period they happened in.
             */
            <ClosePanel
              key={selected.id}
              period={selected}
              moduleLabels={data.moduleLabels}
              onStatusChanged={() => periods.reload()}
            />
          )
        }
      </AsyncPanel>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * One period
 * ------------------------------------------------------------------ */

interface LedgerAction {
  module: string;
  label: string;
  mode: 'close' | 'reopen';
}

function ClosePanel({
  period,
  moduleLabels,
  onStatusChanged,
}: {
  period: Period;
  moduleLabels: Record<string, string>;
  onStatusChanged: () => void;
}) {
  const { can, canAny } = useSession();
  const t = useTerms();
  const destinations = taskDestinations(t);
  const close = useAsync(() => api.get<CloseView>(`/operations/close/${period.id}`), [period.id]);

  const [action, setAction] = useState<LedgerAction | null>(null);
  const [done, setDone] = useState<{ title: string; message: string } | null>(null);

  /*
   * Closing and reopening are two different acts under two different
   * permissions, and the second one always demands a written reason (a period
   * that can be reopened quietly is not a controlled period). They share one
   * mutation so that the dialog has a single busy flag and a single error to
   * show beside the button that caused it.
   */
  const runLedger = useMutation(async (mode: 'close' | 'reopen', module: string, reason: string | null) => {
    if (mode === 'reopen') {
      return api.post<{ periodName: string; module: string; status: string }>(
        `/ledger/periods/${period.id}/status`,
        { module, status: 'open', reason },
      );
    }
    return api.post<{ periodName: string; modulesClosed: string[] }>(`/operations/close/${period.id}/close`, {
      modules: [module],
      force: reason !== null,
      reason: reason ?? undefined,
    });
  });

  const ledgers = Object.entries(moduleLabels).map(([module, label]) => ({
    module,
    label,
    status: period.statuses[module] ?? 'open',
  }));

  return (
    <AsyncPanel state={close}>
      {(data) => {
        /*
         * "Close the period" is itself a blocking task, and it stays
         * outstanding until every ledger is locked. Counting it among the
         * things standing in the way would leave the screen permanently
         * reporting work to do and permanently refusing to close — so the
         * blockers shown here are the prerequisites only, which is the same
         * question the server answers with summary.canClose.
         */
        const blockers = data.blockers.filter((b) => b.code !== 'LOCK_PERIOD');

        const submit = (reason: string | null) => {
          if (!action) return;
          const { mode, label } = action;
          void runLedger.run(mode, action.module, reason).then((result) => {
            // A null result means the mutation failed; its error is already on
            // screen inside the dialog, so the dialog stays open to be retried.
            if (!result) return;
            setAction(null);
            setDone(
              mode === 'close'
                ? {
                    title: `${label} closed for ${result.periodName}`,
                    message:
                      'Postings to that ledger dated in this period will now be refused. Every other ledger is unaffected, and the close is in the audit trail.',
                  }
                : {
                    title: `${label} reopened for ${result.periodName}`,
                    message: 'The ledger will accept postings dated in this period again. Your reason is in the audit trail.',
                  },
            );
            close.reload();
            onStatusChanged();
          });
        };

        return (
          <div className="stack">
            <div className="grid grid--3">
              <Stat label="Close tasks" value={String(data.summary.total)} detail="Checked against live data, not ticked by hand" />
              <Stat label="Complete" value={String(data.summary.complete)} detail={`of ${data.summary.total} on the checklist`} />
              <Stat
                label="Blocking the close"
                value={String(blockers.length)}
                tone={blockers.length > 0 ? 'negative' : 'positive'}
                detail={
                  blockers.length > 0
                    ? 'The period cannot close cleanly until these are cleared'
                    : 'Every blocking task is complete'
                }
              />
            </div>

            {done ? (
              <Alert tone="ok" title={done.title}>
                {done.message}
              </Alert>
            ) : null}

            {blockers.length > 0 ? (
              <Alert
                tone="warn"
                title={`${blockers.length} ${blockers.length === 1 ? 'task stands' : 'tasks stand'} between ${data.period.name} and a clean close`}
              >
                <ul className="smaller" style={{ margin: '0 0 0.5rem', paddingLeft: '1.1rem' }}>
                  {blockers.map((b) => (
                    <li key={b.code}>
                      {b.name}
                      {YES_NO_TASKS.has(b.code) ? null : ` — ${number(b.outstandingCount)} outstanding`}
                    </li>
                  ))}
                </ul>
                <p>Each one is listed below with the value behind it and where to go and clear it.</p>
              </Alert>
            ) : null}

            <Card
              title="Ledger status"
              subtitle="Each ledger closes on its own. Purchase ordering can stay open after the accounting ledgers are locked, so departments are never stopped from raising an order by a month end."
              flush
            >
              <DataTable
                caption={`Period status by ledger for ${period.name}`}
                rows={ledgers}
                rowKey={(l) => l.module}
                columns={[
                  { key: 'ledger', header: 'Ledger', render: (l) => l.label },
                  {
                    key: 'status',
                    header: 'Status',
                    width: '12rem',
                    render: (l) => <Badge tone={statusTone(l.status)}>{statusLabel(l.status)}</Badge>,
                  },
                  {
                    key: 'action',
                    header: <span className="visually-hidden">Close or reopen this ledger</span>,
                    width: '11rem',
                    render: (l) => {
                      if ((l.status === 'open' || l.status === 'closing') && can('close.run')) {
                        return (
                          <button
                            type="button"
                            className="btn btn--sm"
                            onClick={() => setAction({ module: l.module, label: l.label, mode: 'close' })}
                          >
                            Close this ledger
                          </button>
                        );
                      }
                      // A permanently closed period is closed for good; only an
                      // ordinary close can be put back, and only with the
                      // period-control permission.
                      if (l.status === 'closed' && can('gl.period.close')) {
                        return (
                          <button
                            type="button"
                            className="btn btn--sm"
                            onClick={() => setAction({ module: l.module, label: l.label, mode: 'reopen' })}
                          >
                            Reopen
                          </button>
                        );
                      }
                      return null;
                    },
                  },
                ]}
              />
            </Card>

            <Card
              title="Close checklist"
              subtitle="Every line is recomputed from live data each time this screen is opened. Nothing here can be ticked by hand."
              flush
            >
              <DataTable
                caption={`Close tasks for ${data.period.name}`}
                rows={data.tasks}
                rowKey={(task) => task.code}
                emptyMessage="No close tasks are defined for this period."
                columns={[
                  {
                    key: 'task',
                    header: 'Task',
                    render: (task) => (
                      <>
                        <div>{task.name}</div>
                        <div className="smaller muted">{task.description}</div>
                      </>
                    ),
                  },
                  {
                    key: 'area',
                    header: 'Area',
                    width: '9rem',
                    render: (task) => <span className="smaller">{categoryLabel(task.category)}</span>,
                  },
                  {
                    key: 'requirement',
                    header: 'Requirement',
                    render: (task) =>
                      task.severity === 'blocking' ? (
                        <Badge tone="warn">Blocks the close</Badge>
                      ) : (
                        <span className="muted smaller">Advisory</span>
                      ),
                  },
                  {
                    key: 'outstanding',
                    header: 'Outstanding',
                    numeric: true,
                    /*
                     * Shown only while the task is outstanding. A complete task
                     * has nothing left to count, and for the accruals and
                     * prepayments lines the server's value is what HAS been
                     * posted — printing it against a completed row would read
                     * as money still to deal with.
                     */
                    render: (task) =>
                      task.status === 'complete' || YES_NO_TASKS.has(task.code) ? null : (
                        <span>{number(task.outstandingCount)}</span>
                      ),
                  },
                  {
                    key: 'value',
                    header: 'Value',
                    numeric: true,
                    render: (task) =>
                      task.status === 'complete' ? null : <Money minor={task.outstandingValueMinor} blankIfZero />,
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (task) => <Badge tone={statusTone(task.status)}>{statusLabel(task.status)}</Badge>,
                  },
                  {
                    key: 'where',
                    header: 'Where to clear it',
                    width: '10rem',
                    render: (task) => {
                      const destination = destinations[task.code];
                      if (!destination) return <span className="muted smaller">On this screen</span>;
                      if (!canAny(...destination.permissions)) {
                        return <span className="muted smaller">{destination.label}</span>;
                      }
                      return (
                        <Link className="smaller" to={destination.to}>
                          {destination.label} →
                        </Link>
                      );
                    },
                  },
                ]}
              />
            </Card>

            <div className="grid grid--2">
              <ControlAccountsCard reconciliation={data.controlReconciliation} />
              <IntegrityCard check={data.integrityCheck} />
            </div>

            {action ? (
              <LedgerActionDialog
                action={action}
                periodName={data.period.name}
                blockers={blockers}
                busy={runLedger.busy}
                error={runLedger.error}
                onCancel={() => {
                  runLedger.clearError();
                  setAction(null);
                }}
                onConfirm={submit}
              />
            ) : null}
          </div>
        );
      }}
    </AsyncPanel>
  );
}

/* ------------------------------------------------------------------ *
 * The two checks the close depends on
 * ------------------------------------------------------------------ */

function ControlAccountsCard({
  reconciliation,
}: {
  reconciliation: { differences: ControlDifference[]; totalDifferenceMinor: number };
}) {
  const { differences, totalDifferenceMinor } = reconciliation;

  return (
    <Card
      title="Control accounts"
      subtitle="Each sub-ledger is compared with its control account in the general ledger."
      flush
      footer={
        /*
         * Only worth a line when there is more than one ledger adrift. The
         * server totals the differences without regard to their direction, so
         * that a shortfall in one ledger cannot quietly cancel a surplus in
         * another — which is why it is never the arithmetic sum of the column
         * above it, and why a total against a single row would look wrong.
         */
        differences.length > 1 ? (
          <div className="row row--between">
            <span className="smaller muted">Total gap to explain, whichever way each one runs</span>
            <Money minor={totalDifferenceMinor} bold />
          </div>
        ) : undefined
      }
    >
      {differences.length === 0 ? (
        <div style={{ padding: '1.25rem' }}>
          <Alert tone="ok" title="The control accounts agree">
            Both sub-ledgers match the control account behind them, so the aged listings and the general ledger tell the
            same story.
          </Alert>
        </div>
      ) : (
        <DataTable
          caption="Control account differences"
          rows={differences}
          rowKey={(d) => d.ledger}
          columns={[
            { key: 'ledger', header: 'Ledger', render: (d) => d.ledger },
            {
              key: 'control',
              header: 'General ledger',
              numeric: true,
              render: (d) => <Money minor={d.controlBalanceMinor} />,
            },
            {
              key: 'sub',
              header: 'Sub-ledger',
              numeric: true,
              render: (d) => <Money minor={d.subLedgerBalanceMinor} />,
            },
            {
              key: 'difference',
              header: 'Difference',
              numeric: true,
              render: (d) => <Money minor={d.differenceMinor} bold />,
            },
          ]}
        />
      )}
    </Card>
  );
}

function IntegrityCard({ check }: { check: { checked: number; differences: IntegrityDifference[] } }) {
  return (
    <Card
      title="Data integrity"
      subtitle="Every reported balance is recomputed from the journal lines underneath it and the two are compared."
      flush
    >
      {check.differences.length === 0 ? (
        <div style={{ padding: '1.25rem' }}>
          <Alert tone="ok" title={`${number(check.checked)} balances checked, no differences`}>
            Each reported balance still equals the sum of the postings it was built from.
          </Alert>
        </div>
      ) : (
        <DataTable
          caption="Balances that disagree with the postings behind them"
          rows={check.differences}
          rowKey={(d) => `${d.accountId}-${d.periodId}`}
          columns={[
            { key: 'account', header: 'Account', render: (d) => <span className="mono smaller">{d.accountCode}</span> },
            { key: 'period', header: 'Period', render: (d) => <span className="smaller">{d.periodName}</span> },
            {
              key: 'reported',
              header: 'Reported',
              numeric: true,
              render: (d) => <Money minor={d.storedMovementMinor} />,
            },
            {
              key: 'derived',
              header: 'From the postings',
              numeric: true,
              render: (d) => <Money minor={d.derivedMovementMinor} />,
            },
            {
              key: 'difference',
              header: 'Difference',
              numeric: true,
              render: (d) => <Money minor={d.differenceMinor} bold />,
            },
          ]}
        />
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Closing and reopening a ledger
 * ------------------------------------------------------------------ */

/**
 * The confirmation.
 *
 * Closing a ledger changes what the system will accept tomorrow, so it is
 * never a single unguarded click. Where something is still outstanding the
 * dialog names it and asks for a reason, because the alternative — a button
 * that is simply disabled — tells the Finance Director she cannot close
 * without telling her what to do about it, and there are month ends where the
 * right answer is to close anyway and say why.
 */
function LedgerActionDialog({
  action,
  periodName,
  blockers,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  action: LedgerAction;
  periodName: string;
  blockers: Array<{ code: string; name: string; outstandingCount: number }>;
  busy: boolean;
  error: { code: string; message: string } | null;
  onCancel: () => void;
  onConfirm: (reason: string | null) => void;
}) {
  const [reason, setReason] = useState('');

  const ledger = action.label.toLowerCase();
  const reopening = action.mode === 'reopen';
  // Reopening always needs a reason — the server refuses without one. Closing
  // needs one only when it is an override of something still outstanding.
  const needsReason = reopening || blockers.length > 0;
  const tooShort = needsReason && reason.trim().length < 10;

  return (
    <Modal
      open
      title={reopening ? `Reopen the ${ledger} for ${periodName}` : `Close the ${ledger} for ${periodName}`}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={blockers.length > 0 && !reopening ? 'btn btn--danger' : 'btn btn--primary'}
            disabled={busy || tooShort}
            title={tooShort ? 'Give the reason first. It is kept in the audit trail.' : undefined}
            onClick={() => onConfirm(needsReason ? reason.trim() : null)}
          >
            {busy ? 'Working…' : reopening ? `Reopen the ${ledger}` : `Close the ${ledger}`}
          </button>
        </>
      }
    >
      <div className="stack">
        {reopening ? (
          <p>
            {periodName} will accept postings to the {ledger} again. Every other ledger keeps the status it has now, and
            the reopening is recorded against your name.
          </p>
        ) : (
          <p>
            Postings to the {ledger} dated in {periodName} will be refused after this. Every other ledger carries on
            unaffected, and the period can still be reported on.
          </p>
        )}

        {blockers.length > 0 && !reopening ? (
          <Alert tone="warn" title="This period still has work outstanding">
            <ul className="smaller" style={{ margin: '0 0 0.5rem', paddingLeft: '1.1rem' }}>
              {blockers.map((b) => (
                <li key={b.code}>{b.name}</li>
              ))}
            </ul>
            <p>Closing now is an override. The reason you give is kept in the audit trail with your name against it.</p>
          </Alert>
        ) : null}

        {needsReason ? (
          <TextArea
            label={reopening ? 'Why is the period being reopened?' : 'Why is the ledger being closed with items outstanding?'}
            hint="A sentence an auditor reading this in a year's time would accept."
            required
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            error={reason.trim().length > 0 && tooShort ? 'Please give a little more detail than that.' : null}
          />
        ) : (
          <p className="muted smaller">Every blocking task on the checklist is complete.</p>
        )}

        <ErrorNotice error={error} />
      </div>
    </Modal>
  );
}
